package org.vreo.devicehub.features.settings.presentation.settings

import org.koin.compose.viewmodel.koinViewModel

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Build
import androidx.compose.material.icons.filled.CenterFocusStrong
import androidx.compose.material.icons.filled.ErrorOutline
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Save
import androidx.compose.material.icons.filled.Tune
import androidx.compose.material.icons.filled.Videocam
import androidx.compose.material.icons.filled.WbSunny
import androidx.compose.material.icons.filled.ZoomIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.domain.device.device.anprDeviceSettings.AnprDeviceUpdateReleaseType
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.VideoFrameRotation
import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.core.presentation.components.configTextField.ConfigTextField
import org.vreo.devicehub.features.core.presentation.components.largeDropdownMenu.LargeDropdownMenu
import org.vreo.devicehub.features.core.presentation.roundToDecimals
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.settings.presentation.settings.components.toUiText
import org.vreo.devicehub.features.settings.presentation.settings.components.ControlSlider

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.brightness_control_title
import devicehub.composeapp.generated.resources.control_screen_title
import devicehub.composeapp.generated.resources.device_control_unavailable_label
import devicehub.composeapp.generated.resources.device_general_settings_title
import devicehub.composeapp.generated.resources.device_update_type
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.focus_control_title
import devicehub.composeapp.generated.resources.load_config_button_title
import devicehub.composeapp.generated.resources.save_button_title
import devicehub.composeapp.generated.resources.single_shot_autofocus_button_label
import devicehub.composeapp.generated.resources.video_control_brightness_title
import devicehub.composeapp.generated.resources.video_control_exposure_max_title
import devicehub.composeapp.generated.resources.video_control_exposure_min_title
import devicehub.composeapp.generated.resources.video_control_frame_rotation_title
import devicehub.composeapp.generated.resources.video_control_gain_max_title
import devicehub.composeapp.generated.resources.video_control_gain_min_title
import devicehub.composeapp.generated.resources.video_control_title
import devicehub.composeapp.generated.resources.zoom_control_title
import org.vreo.devicehub.features.core.presentation.components.sectionHeader.SectionHeader


/**
 * Device settings, displays device configuration screen.
 *
 * @param viewModel view model for video control screen
 */
@Composable
fun SettingsScreenRoot(
    viewModel: SettingsViewModel = koinViewModel(),
) {
    val state: SettingsState by viewModel.state.collectAsStateWithLifecycle()

    DeviceSettingsView(
        state = state,
        onAction = { action ->
            viewModel.onAction(action)
        }
    )
}

/**
 * Device settings view, displays available configuration options
 *
 * @param state state of video control screen
 * @param onAction action handler
 */
@Composable
fun DeviceSettingsView(
    state: SettingsState,
    onAction: (SettingsActions) -> Unit,
) {
    // Handling dialogs
    if (state.isLoading) LoadingDialog()
    state.error?.let { error ->
        ErrorDialog(
            dialogTitle = stringResource(Res.string.error_general_title),
            dialogText = error.asString(),
            onDismissRequest = { onAction(SettingsActions.OnClearError) },
            onConfirmation = { onAction(SettingsActions.OnClearError) }
        )
    }

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(20.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        // Screen title with icon
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(14.dp)
        ) {
            Surface(
                shape = CircleShape,
                color = MaterialTheme.colorScheme.primaryContainer,
                modifier = Modifier.size(44.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = Icons.Default.Tune,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onPrimaryContainer,
                        modifier = Modifier.size(24.dp)
                    )
                }
            }
            Text(
                text = stringResource(Res.string.control_screen_title),
                style = MaterialTheme.typography.headlineSmall,
            )
        }

        // Device unavailable warning banner
        if (!state.isDeviceActive) {
            Surface(
                color = MaterialTheme.colorScheme.errorContainer,
                shape = MaterialTheme.shapes.large,
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.ErrorOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onErrorContainer
                    )
                    Text(
                        text = stringResource(Res.string.device_control_unavailable_label),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onErrorContainer
                    )
                }
            }
        }

        // Video settings card
        OutlinedCard(
            shape = MaterialTheme.shapes.medium,
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(
                verticalArrangement = Arrangement.spacedBy(16.dp),
                modifier = Modifier.padding(16.dp),
            ) {
                SectionHeader(
                    icon = { Icon(Icons.Default.Videocam, contentDescription = null) },
                    title = stringResource(Res.string.video_control_title)
                )

                LargeDropdownMenu(
                    label = stringResource(Res.string.video_control_frame_rotation_title),
                    items = VideoFrameRotation.entries.map { it.toUiText().asString() },
                    selectedIndex = state.selectedFrameRotationIndex,
                    onItemSelected = { onAction(SettingsActions.OnFrameRotationSelected(it)) },
                )
            }
        }

        // Zoom and focus card
        if (state.zoomEnabled || state.focusEnabled) {
            OutlinedCard(
                shape = MaterialTheme.shapes.medium,
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    modifier = Modifier.padding(16.dp)
                ) {
                    SectionHeader(
                        icon = { Icon(Icons.Default.ZoomIn, contentDescription = null) },
                        title = "Optics"
                    )

                    if (state.zoomEnabled) {
                        ControlSlider(
                            label = stringResource(Res.string.zoom_control_title),
                            sliderPositon = state.zoom,
                            sliderMax = state.zoomMax,
                            sliderMin = state.zoomMini,
                            onSliderPositionChange = {
                                onAction(SettingsActions.OnZoomPositionChanged(it.toString()))
                            },
                            onDone = { onAction(SettingsActions.OnSaveClicked) },
                            onTextChange = { onAction(SettingsActions.OnZoomPositionChanged(it)) },
                            modifier = Modifier.fillMaxWidth()
                        )
                    }

                    if (state.zoomEnabled && state.focusEnabled) {
                        HorizontalDivider()
                    }

                    if (state.focusEnabled) {
                        ControlSlider(
                            label = stringResource(Res.string.focus_control_title),
                            sliderPositon = state.focus,
                            sliderMax = state.focusMax,
                            sliderMin = state.focusMini,
                            onSliderPositionChange = {
                                onAction(SettingsActions.OnFocusPositionChanged(it.toString()))
                            },
                            onDone = { onAction(SettingsActions.OnSaveClicked) },
                            onTextChange = { onAction(SettingsActions.OnFocusPositionChanged(it)) },
                            modifier = Modifier.fillMaxWidth()
                        )

                        FilledTonalButton(
                            onClick = { onAction(SettingsActions.OnSingleShotAutofocusClicked) },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Icon(
                                imageVector = Icons.Default.CenterFocusStrong,
                                contentDescription = null,
                                modifier = Modifier.size(ButtonDefaults.IconSize)
                            )
                            Spacer(Modifier.width(ButtonDefaults.IconSpacing))
                            Text(stringResource(Res.string.single_shot_autofocus_button_label))
                        }
                    }
                }
            }
        }

        // Brightness settings card
        OutlinedCard(
            shape = MaterialTheme.shapes.medium,
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(
                verticalArrangement = Arrangement.spacedBy(16.dp),
                modifier = Modifier.padding(16.dp)
            ) {
                SectionHeader(
                    icon = { Icon(Icons.Default.WbSunny, contentDescription = null) },
                    title = stringResource(Res.string.brightness_control_title)
                )

                ConfigTextField(
                    label = stringResource(Res.string.video_control_brightness_title),
                    min = state.brightnessLimitMini.roundToDecimals().toString(),
                    max = state.brightnessLimitMax.roundToDecimals().toString(),
                    value = state.brightness,
                    onValueChange = { onAction(SettingsActions.OnBrightnessChanged(it)) },
                )

                HorizontalDivider()

                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    ConfigTextField(
                        label = stringResource(Res.string.video_control_exposure_max_title),
                        min = state.exposureTimeLimitMini.toString(),
                        max = state.exposureTimeLimitMax.toString(),
                        value = state.exposureTimeMax,
                        onValueChange = {
                            onAction(SettingsActions.OnExposureTimeChanged(it, true))
                        },
                    )
                    ConfigTextField(
                        label = stringResource(Res.string.video_control_exposure_min_title),
                        min = state.exposureTimeLimitMini.toString(),
                        max = state.exposureTimeLimitMax.toString(),
                        value = state.exposureTimeMini,
                        onValueChange = {
                            onAction(SettingsActions.OnExposureTimeChanged(it, false))
                        }
                    )
                }

                HorizontalDivider()

                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    ConfigTextField(
                        label = stringResource(Res.string.video_control_gain_max_title),
                        min = state.gainLimitMini.roundToDecimals().toString(),
                        max = state.gainLimitMax.roundToDecimals().toString(),
                        value = state.gainMax,
                        onValueChange = { onAction(SettingsActions.OnGainChanged(it, true)) },
                    )
                    ConfigTextField(
                        label = stringResource(Res.string.video_control_gain_min_title),
                        min = state.gainLimitMini.roundToDecimals().toString(),
                        max = state.gainLimitMax.roundToDecimals().toString(),
                        value = state.gainMini,
                        onValueChange = { onAction(SettingsActions.OnGainChanged(it, false)) }
                    )
                }
            }
        }

        // Admin — general device settings card
        if (state.isAdmin) {
            OutlinedCard(
                shape = MaterialTheme.shapes.medium,
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    modifier = Modifier.padding(16.dp),
                ) {
                    SectionHeader(
                        icon = { Icon(Icons.Default.Build, contentDescription = null) },
                        title = stringResource(Res.string.device_general_settings_title)
                    )

                    LargeDropdownMenu(
                        label = stringResource(Res.string.device_update_type),
                        items = AnprDeviceUpdateReleaseType.entries.map { it.toUiText().asString() },
                        selectedIndex = state.selectedUpdateReleaseTypeIndex,
                        onItemSelected = { onAction(SettingsActions.OnUpdateReleaseTypeSelected(it)) },
                    )
                }
            }
        }

        // Action footer
        Column(
            verticalArrangement = Arrangement.spacedBy(10.dp),
            modifier = Modifier.fillMaxWidth(),
        ) {
            FilledTonalButton(
                enabled = state.isDeviceActive,
                onClick = { onAction(SettingsActions.OnGetDeviceConfigClicked) },
                modifier = Modifier.fillMaxWidth()
            ) {
                Icon(
                    imageVector = Icons.Default.Refresh,
                    contentDescription = null,
                    modifier = Modifier.size(ButtonDefaults.IconSize)
                )
                Spacer(Modifier.width(ButtonDefaults.IconSpacing))
                Text(stringResource(Res.string.load_config_button_title))
            }

            Button(
                enabled = state.isDeviceActive,
                onClick = { onAction(SettingsActions.OnSaveClicked) },
                modifier = Modifier.fillMaxWidth()
            ) {
                Icon(
                    imageVector = Icons.Default.Save,
                    contentDescription = null,
                    modifier = Modifier.size(ButtonDefaults.IconSize)
                )
                Spacer(Modifier.width(ButtonDefaults.IconSpacing))
                Text(stringResource(Res.string.save_button_title))
            }
        }

        Spacer(modifier = Modifier.height(32.dp))
    }
}

@Preview
@Composable
fun SettingsPreview() {
    MaterialTheme {
        Scaffold {
            DeviceSettingsView(
                state = SettingsState(
                    zoomEnabled = true,
                    zoom = 0f,
                    zoomMini = 0f,
                    zoomMax = 100f,
                    focusEnabled = true,
                    focus = 0f,
                    focusMini = 0f,
                    focusMax = 100f
                ),
                onAction = {}
            )
        }
    }
}
